import { useCallback } from "react";
import { WarningTwoTone } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { modal } from "Components/General/Modal/AntdModal";
import { EmailSubmitStore } from "Pages/Project/Communication/Stores/EmailSubmitStore";

type UseEmailAttachmentsReturn = {
  mapAttachments: (attachments: API_FileAttachment[]) => void;
  checkAttachmentsAccess: (attachments: API_FileAttachment[]) => void;
};

export const useEmailAttachments = (emailSubmitStore: EmailSubmitStore): UseEmailAttachmentsReturn => {
  const { t } = useTranslation();

  const mapAttachments = useCallback(
    (attachments: API_FileAttachment[]) => {
      attachments.forEach((item) => {
        // if file comes from workflow transform to API_FileAttachment interface
        if ((item as API_FileInfo).fileName) {
          const originalFile: API_FileInfo = item;
          const fileInfoToAttachment: API_FileAttachment = {
            name: originalFile?.fileName,
            id: originalFile?.id,
            type: originalFile?.fileType,
            size: originalFile?.fileSize,
          };
          emailSubmitStore.addProjectFile(fileInfoToAttachment);
        } else {
          emailSubmitStore.addProjectFile(item);
        }
      });
    },
    [emailSubmitStore]
  );

  /**
   * This method checks attachments that don't have access right "DOWNLOAD" because users should not forward
   * attachments that they don't have full access to them.
   */
  const checkAttachmentsAccess = useCallback(
    (attachments: API_FileAttachment[]) => {
      const attachmentsWithoutDownloadAccess = attachments.some(
        // file was passed from documents table does not have accessLevel property -
        // check fileActions.download of "FileDataEntity"
        (item) => {
          if (!item.accessLevel) {
            if ((item as API_FileDataEntity).fileActions) {
              return !(item as API_FileDataEntity).fileActions.download;
            } else if (item as API_FileInfo) {
              return false;
            } else {
              return true;
            }
          } else {
            return item.accessLevel !== "DOWNLOAD";
          }
        }
      );

      if (attachmentsWithoutDownloadAccess) {
        const setOnlyDownloadable = () => {
          const downloadableFiles = attachments.reduce(
            (buffer, current) => {
              if (current.accessLevel !== "DOWNLOAD") {
                buffer.viewOnly.push(current);
              } else {
                buffer.downloadable.push(current);
              }
              return buffer;
            },
            { viewOnly: [], downloadable: [] }
          );

          // if "downloadable" array is empty - we still show this modal
          // because such use case wasn't described in DEV-3568
          modal.confirm({
            title: t("Some files may not be forwarded"),
            zIndex: 1011,
            icon: <WarningTwoTone twoToneColor="#faad14" />,
            width: "100%",
            style: { maxWidth: 600 },
            content: (
              <div>
                <p>{t("The following files can't be forwarded because you don't have download rights for them:")}</p>
                <ul>
                  {downloadableFiles.viewOnly.map((file) => (
                    <li key={file.id}>{file.name}</li>
                  ))}
                </ul>
                <p>{t("The following files can be forwarded because you have download rights for them:")}</p>
                <ul>
                  {downloadableFiles.downloadable.map((file) => (
                    <li key={file.id}>{file.name}</li>
                  ))}
                </ul>
              </div>
            ),
            onOk: () => {
              mapAttachments(downloadableFiles.downloadable);
            },
          });
        };
        setOnlyDownloadable();
      } else {
        mapAttachments(attachments);
      }
    },
    [mapAttachments]
  );

  return { mapAttachments, checkAttachmentsAccess };
};
